// src/Pages/Signup.js
import React, { useState } from "react";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { auth, provider, db } from "../firebase";
import { collection, doc, setDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, User } from "lucide-react";

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showPhonePopup, setShowPhonePopup] = useState(false); // For Google phone popup

  const navigate = useNavigate();

  // Validate Nepali mobile number (10 digits)
  const isValidPhone = (num) => /^[0-9]{10}$/.test(num);

  // Google SignIn
  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(auth, provider);
      setEmail(result.user.email || "");
      setFullName(result.user.displayName || "");
      setShowPhonePopup(true); // Show popup to add phone
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Save phone number for Google user and store in Firestore
  const handleSavePhone = async () => {
    if (!isValidPhone(phone)) {
      setError("Please enter a valid 10-digit Nepali mobile number.");
      return;
    }
    try {
      if (auth.currentUser) {
        // Update profile phone
        await updateProfile(auth.currentUser, {
          phoneNumber: "+977" + phone,
        });

        // Save to Firestore users collection
        await setDoc(doc(db, "users", auth.currentUser.uid), {
          uid: auth.currentUser.uid,
          fullName: fullName,
          email: email,
          phone: "+977" + phone,
          createdAt: new Date(),
        });

        setShowPhonePopup(false);
        navigate("/"); // Redirect to home
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Email/Password Signup + Firestore + email verification
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    if (!isValidPhone(phone)) {
      setError("Please enter a valid 10-digit Nepali mobile number.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: fullName, phoneNumber: "+977" + phone });

      // Store user in Firestore
      await setDoc(doc(db, "users", userCredential.user.uid), {
        uid: userCredential.user.uid,
        fullName: fullName,
        email: email,
        phone: "+977" + phone,
        createdAt: new Date(),
      });

      await sendEmailVerification(userCredential.user);
      await signOut(auth);
      setEmailSent(true);
      setMessage(
        "Verification email sent! Please check your inbox and verify your email before logging in."
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-100 to-purple-200 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-6 text-center">
          <h2 className="text-3xl font-bold text-gray-800">Verify Your Email</h2>
          <p className="text-gray-600">
            We have sent a verification link to <strong>{email}</strong>. Please check your inbox and verify your email before logging in.
          </p>
          <p className="mt-4">
            <Link to="/login" className="text-blue-600 hover:underline font-semibold">
              Go to Login
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-100 to-purple-200 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-6 relative">
        <h2 className="text-3xl font-bold text-center text-gray-800">Create Account</h2>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading || emailSent}
          className={`flex items-center justify-center gap-3 w-full py-2 border rounded-lg shadow-sm hover:bg-gray-100 transition duration-200 ${
            emailSent ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <img
            src="https://www.svgrepo.com/show/475656/google-color.svg"
            alt="Google"
            className="w-5 h-5"
          />
          {loading ? "Signing in..." : "Sign Up with Google"}
        </button>

        <div className="flex items-center gap-4">
          <hr className="flex-grow border-gray-300" />
          <span className="text-gray-500 text-sm">or</span>
          <hr className="flex-grow border-gray-300" />
        </div>

        {error && <p className="text-red-600 text-sm text-center">{error}</p>}
        {message && <p className="text-green-600 text-sm text-center">{message}</p>}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Doe"
              className="w-full px-4 py-2 mt-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={emailSent}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              className="w-full px-4 py-2 mt-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={emailSent}
            />
          </div>

          {/* Nepali Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Nepali Number</label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-600">
                +977
              </span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="981XXXXXXX"
                pattern="[0-9]{10}"
                className="w-full px-4 py-2 border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={emailSent}
              />
            </div>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-2 mt-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              required
              disabled={emailSent}
            />
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </div>
          </div>

          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 py-2 mt-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition duration-200"
            disabled={loading || emailSent}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Signing up...
              </>
            ) : (
              <>
                <User size={18} />
                Sign Up
              </>
            )}
          </button>
        </form>

        <p className="text-center text-sm text-gray-600 mt-4">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:underline">
            Login
          </Link>
        </p>

        {/* Google Phone Number Popup */}
        {showPhonePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-2xl w-full max-w-sm text-center space-y-4">
              <h2 className="text-xl font-bold text-gray-800">Add Your Phone Number</h2>
              <p className="text-gray-600 text-sm">
                Your phone number will be used for delivery purposes.
              </p>
              <div className="flex mt-2">
                <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-600">
                  +977
                </span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="981XXXXXXX"
                  pattern="[0-9]{10}"
                  className="w-full px-4 py-2 border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex justify-center mt-4 gap-4">
                <button
                  onClick={handleSavePhone}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Save & Continue
                </button>
              </div>
              {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
